import { COLORS, LIFT_PARTS } from '../theme.js';
import { EARTH } from '../view/atmosphere.js';

/**
 * Under the street, on the home screen (ADR-0028).
 *
 * The Transit Centre's metro is below the pavement, so its first shifts are
 * drawn there: in the ground, under the street the buildings stand on. And the
 * ground is alive the way the street is — water in the mains, a worm in the
 * soil, and a metro train through the tunnel at the bottom every so often.
 *
 * Scenery, and quiet by the street's rule: earth and iron, the people's muted
 * cream and rose for the train, nothing a floor's colour (`UNDERGROUND_ART`,
 * measured in places.spec.ts). Under reduced motion it all holds still.
 */

const css = (colour: number): string => `#${colour.toString(16).padStart(6, '0')}`;
const INK = css(COLORS.ink);
const CREAM = css(COLORS.cabin);
const GLASS = '#d6e6ee';
const GLOW = css(LIFT_PARTS.glow);
const ROSE = css(LIFT_PARTS.rug);
const PIPE = '#8b9aa0';
const TUNNEL = '#4a4440';
const RING = '#6e6660';
const RAIL = '#9a948c';
const WORM = '#d9a8a0';
const WATER = '#bcd3dc';
const SOIL = css(EARTH.soil);
const TOPSOIL = css(EARTH.topsoil);
const ROOT = css(EARTH.root);
const STONE = css(EARTH.stone);
const STONE_EDGE = css(EARTH.stoneEdge);

const line = `stroke="${INK}" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round"`;

/**
 * Just under the pavement: the topsoil the planting's roots reach into, a
 * water main with a joint that weeps, and a bundle of cables. 400 × 40, drawn
 * across the screen.
 */
export const UTILITIES = `<svg viewBox="0 0 400 40" preserveAspectRatio="none" aria-hidden="true">
  <rect width="400" height="40" fill="${SOIL}"/>
  <rect width="400" height="9" fill="${TOPSOIL}"/>
  <path d="M38 0 C36 8 30 12 26 22 M36 7 C42 12 44 18 48 24 M318 0 C320 10 326 14 330 24 M321 9 C314 14 312 20 306 26" fill="none" stroke="${ROOT}" stroke-width="1.6" stroke-linecap="round"/>
  <rect x="0" y="16" width="400" height="9" fill="${PIPE}" ${line}/>
  <path d="M0 18.5 H400" stroke="${CREAM}" stroke-width="1.2" opacity="0.45"/>
  <rect x="94" y="14" width="10" height="13" rx="2" fill="${PIPE}" ${line}/>
  <rect x="244" y="14" width="10" height="13" rx="2" fill="${PIPE}" ${line}/>
  <path d="M0 33 H400 M0 36 H400" stroke="${ROOT}" stroke-width="2.2"/>
  <circle cx="168" cy="6" r="3" fill="${STONE}" stroke="${STONE_EDGE}" stroke-width="0.8"/>
  <circle cx="372" cy="31" r="2.4" fill="${STONE}" stroke="${STONE_EDGE}" stroke-width="0.8"/>
</svg>`;

/** A drop from the main's joint, falling and gone. */
export const DRIP = `<svg viewBox="0 0 6 8" width="6" height="8" aria-hidden="true"><path d="M3 0 C5 3 6 5 3 8 C0 5 1 3 3 0z" fill="${WATER}" stroke="${INK}" stroke-width="0.6"/></svg>`;

/** A worm in the soil, its body in two halves that take turns. */
export const WORM_ART = `<svg viewBox="0 0 26 10" width="26" height="10" aria-hidden="true">
  <path class="worm-a" d="M2 6 Q7 1 12 6" fill="none" stroke="${WORM}" stroke-width="3.2" stroke-linecap="round"/>
  <path class="worm-b" d="M12 6 Q17 10 22 5" fill="none" stroke="${WORM}" stroke-width="3.2" stroke-linecap="round"/>
  <circle cx="22.5" cy="4.6" r="0.9" fill="${INK}"/>
</svg>`;

/**
 * The tunnel at the bottom of the ground: its rings, the track, the lamps.
 * 400 × 60, across the screen.
 */
export const TUNNEL_ART = `<svg viewBox="0 0 400 60" preserveAspectRatio="none" aria-hidden="true">
  <rect width="400" height="60" fill="${SOIL}"/>
  <rect y="6" width="400" height="50" rx="6" fill="${TUNNEL}" stroke="${INK}" stroke-width="1.5"/>
  <path d="M0 9 H400 M0 53 H400" stroke="${RING}" stroke-width="2"/>
  <path d="M40 9 V53 M100 9 V53 M160 9 V53 M220 9 V53 M280 9 V53 M340 9 V53" stroke="${RING}" stroke-width="1.4" opacity="0.8"/>
  <path d="M0 49 H400" stroke="${RAIL}" stroke-width="2"/>
  <path d="M10 51 v3 M30 51 v3 M50 51 v3 M70 51 v3 M90 51 v3 M110 51 v3 M130 51 v3 M150 51 v3 M170 51 v3 M190 51 v3 M210 51 v3 M230 51 v3 M250 51 v3 M270 51 v3 M290 51 v3 M310 51 v3 M330 51 v3 M350 51 v3 M370 51 v3 M390 51 v3" stroke="${RAIL}" stroke-width="2"/>
  <circle cx="70" cy="13" r="2.4" fill="${GLOW}"/><circle cx="190" cy="13" r="2.4" fill="${GLOW}"/><circle cx="310" cy="13" r="2.4" fill="${GLOW}"/>
</svg>`;

/** The metro: three cars, cream with a rose stripe, lit windows. */
export const METRO = `<svg viewBox="0 0 252 30" width="252" height="30" aria-hidden="true">
  ${[0, 84, 168]
    .map(
      (x) => `<path d="M${x + 3} 26V6q0-3 3-3h72q3 0 3 3v20z" fill="${CREAM}" ${line}/>
  <rect x="${x + 3}" y="17" width="78" height="3" fill="${ROSE}"/>
  <path d="M${x + 9} 7h12v7h-12zM${x + 25} 7h12v7h-12zM${x + 47} 7h12v7h-12zM${x + 63} 7h12v7h-12z" fill="${GLASS}" stroke="${INK}" stroke-width="1"/>
  <path d="M${x + 40} 6v19" stroke="${INK}" stroke-width="1"/>`,
    )
    .join('')}
  <path d="M249 26V9q0-6 -3-6h-3v23z" fill="${CREAM}" ${line}/>
  <rect x="245" y="20" width="3" height="2" rx="1" fill="${GLOW}"/>
</svg>`;

/** Everything drawn under the street, for the colour test. */
export const UNDERGROUND_ART = [UTILITIES, DRIP, WORM_ART, TUNNEL_ART, METRO].join('\n');
